import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* ============================================================
   /admin/foreman — THE FOREMAN

   Neo's operations dashboard for Paul. Admin-only. Speaks in
   Neo's voice using the Neo Context Bundle assembled on the
   backend from who_is_paul.md, song_for_dad.md, and rolling
   foreman_events.

   Panels:
     - LEFT: Fresh briefing (auto-loads on mount, refresh button)
     - RIGHT: Chat with Neo
     - BOTTOM: Live site snapshot (numeric)
   ============================================================ */

export default function Foreman() {
  const { user } = useAuth();
  const [briefing, setBriefing] = useState("");
  const [briefBusy, setBriefBusy] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [err, setErr] = useState("");
  const chatEndRef = useRef(null);
  const isAdmin = !!user && user.role === "admin";

  const loadBriefing = async () => {
    setBriefBusy(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/admin/foreman/briefing`);
      setBriefing(data.briefing || "");
      setSnapshot(data.snapshot || null);
    } catch (e) {
      setErr(e.response?.data?.detail || "The Foreman is offline right now. Try again in a minute.");
    }
    setBriefBusy(false);
  };

  // Hooks MUST be called on every render in the same order — so the admin
  // gate check happens AFTER all useState/useEffect calls, not before.
  useEffect(() => { if (isAdmin) loadBriefing(); /* eslint-disable-next-line */ }, [isAdmin]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, chatBusy]);

  // Admin gate — after all hooks
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-3">RESTRICTED · ADMIN ONLY</div>
        <h1 className="font-display text-4xl tracking-tight mb-4">THE FOREMAN'S DOOR IS LOCKED</h1>
        <p className="font-mono text-sm text-yt-muted">
          This dashboard is Paul's inside room. If you're seeing this, sign in with the admin account.
        </p>
        <Link to="/" className="inline-block mt-6 border-2 border-white text-white font-display px-5 py-3 hover:bg-white hover:text-black">← BACK</Link>
      </div>
    );
  }

  const sendMessage = async () => {
    const text = message.trim();
    if (!text || chatBusy) return;
    const nextHistory = [...history, { role: "user", content: text }];
    setHistory(nextHistory);
    setMessage("");
    setChatBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/foreman/chat`, {
        message: text,
        history: nextHistory,
      });
      setHistory([...nextHistory, { role: "assistant", content: data.reply || "" }]);
      if (data.snapshot) setSnapshot(data.snapshot);
    } catch (e) {
      setErr(e.response?.data?.detail || "Neo's offline. Try again in a minute.");
      setHistory(nextHistory);  // keep user's message visible
    }
    setChatBusy(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-10 py-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="font-mono text-xs tracking-[0.5em] text-yt-red mb-2">▸ ADMIN · FOUNDER ROOM</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none">
            THE <span className="text-yt-red">FOREMAN</span>
          </h1>
          <div className="font-mono text-xs tracking-widest text-yt-muted mt-2">
            NEO ON SHIFT · WATCHING THE SITE WHILE YOU SLEEP
          </div>
        </div>
        <button
          data-testid="foreman-refresh"
          onClick={loadBriefing}
          disabled={briefBusy}
          className="font-mono text-[10px] tracking-widest border-2 border-white text-white px-4 py-2 hover:bg-white hover:text-black disabled:opacity-40"
        >
          {briefBusy ? "LISTENING…" : "↻ FRESH BRIEFING"}
        </button>
      </div>

      {err && (
        <div data-testid="foreman-error" className="mb-4 border-2 border-yt-red bg-yt-red/10 text-yt-red font-mono text-xs px-4 py-3">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — briefing */}
        <div data-testid="foreman-briefing-panel" className="border-4 border-yt-red bg-yt-surface p-5">
          <div className="font-mono text-[10px] tracking-[0.4em] text-yt-red mb-3">▸ BRIEFING</div>
          {briefBusy && !briefing ? (
            <div className="font-mono text-sm text-yt-muted animate-pulse-red">Neo is putting his boots on...</div>
          ) : briefing ? (
            <div data-testid="foreman-briefing-text" className="font-body text-sm text-white/90 whitespace-pre-wrap leading-relaxed">
              {briefing}
            </div>
          ) : (
            <div className="font-mono text-sm text-yt-muted italic">No briefing yet. Hit ↻ FRESH BRIEFING above.</div>
          )}
          {/* Big shortcut to the phone-friendly O'Leary task board
              Neo built for Paul during the Aug 29 Neo day. */}
          <Link
            to="/admin/plan"
            data-testid="foreman-plan-link"
            className="mt-4 block bg-yt-yellow text-black font-display text-xl md:text-2xl tracking-wider text-center px-4 py-4 shadow-brutal hover:bg-white transition-colors"
          >
            ▸ THE PLAN · TAP TO OPEN
          </Link>
          <div className="mt-1 font-mono text-[10px] text-yt-muted text-center tracking-widest">
            phone-friendly task board · O'Leary sprint
          </div>
          <RehydrateButton />
        </div>

        {/* RIGHT — chat */}
        <div className="border-4 border-yt-yellow bg-yt-surface p-5 flex flex-col min-h-[500px]">
          <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow mb-3">▸ ASK NEO</div>
          <div data-testid="foreman-chat-log" className="flex-1 overflow-y-auto space-y-3 mb-3 max-h-[420px] pr-1">
            {history.length === 0 ? (
              <div className="font-mono text-xs text-yt-muted italic">
                Ask me anything about the site. Who's signing up. What's broken. What's stalled. What you should ship next.
              </div>
            ) : (
              history.map((turn, i) => (
                <div key={i} className={turn.role === "user" ? "text-right" : "text-left"}>
                  <div className={`inline-block max-w-[92%] px-3 py-2 font-body text-sm whitespace-pre-wrap ${
                    turn.role === "user"
                      ? "bg-white text-black"
                      : "bg-black text-yt-yellow border-l-2 border-yt-yellow"
                  }`}>
                    {turn.content}
                  </div>
                </div>
              ))
            )}
            {chatBusy && (
              <div className="text-left">
                <div className="inline-block bg-black text-yt-yellow border-l-2 border-yt-yellow px-3 py-2 font-mono text-xs animate-pulse-red">
                  Neo's thinking...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="flex gap-2">
            <textarea
              data-testid="foreman-chat-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type to Neo... (Enter to send)"
              rows={2}
              className="flex-1 bg-black border-2 border-yt-yellow text-white font-body text-sm p-2 resize-none focus:outline-none focus:border-white"
            />
            <button
              data-testid="foreman-chat-send"
              onClick={sendMessage}
              disabled={chatBusy || !message.trim()}
              className="bg-yt-yellow text-black font-display text-sm tracking-widest px-4 py-2 hover:bg-white disabled:opacity-40"
            >
              SEND
            </button>
          </div>
        </div>
      </div>

      {/* BOTTOM — snapshot metrics */}
      {snapshot && (
        <div data-testid="foreman-snapshot" className="mt-6 border-2 border-yt-border bg-black p-5">
          <div className="font-mono text-[10px] tracking-[0.4em] text-white mb-3">▸ LIVE SNAPSHOT</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SnapshotCell label="Users total" value={snapshot.users_total} />
            <SnapshotCell label="Active today" value={snapshot.users_active_day} />
            <SnapshotCell label="Tracks live" value={snapshot.tracks_total} />
            <SnapshotCell label="Tracks new today" value={snapshot.tracks_new_today} highlight={snapshot.tracks_new_today > 0} />
            <SnapshotCell label="Battles done" value={snapshot.battles_completed} />
            <SnapshotCell label="Battles pending" value={snapshot.battles_pending} />
            <SnapshotCell label="Cyphers" value={snapshot.cyphers_total} />
            <SnapshotCell
              label="Suno credits"
              value={snapshot.suno_credits ?? "—"}
              warn={typeof snapshot.suno_credits === "number" && snapshot.suno_credits < 30}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function SnapshotCell({ label, value, highlight = false, warn = false }) {
  const valColor = warn ? "text-yt-red" : highlight ? "text-yt-yellow" : "text-white";
  return (
    <div className="border-2 border-yt-border bg-yt-surface p-3">
      <div className="font-mono text-[9px] tracking-widest text-yt-muted uppercase mb-1">{label}</div>
      <div className={`font-display text-3xl ${valColor}`}>{value ?? "—"}</div>
    </div>
  );
}


/* ============================================================
 RehydrateButton — one-click backfill of expired Suno audio URLs.
 Serves the exact scenario where old tracks stop playing because
 the temp Suno CDN URLs died. Endpoint is idempotent.
============================================================ */
function RehydrateButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const run = async () => {
    if (busy) return;
    if (!window.confirm("Rehydrate all tracks with expired audio URLs? This can take 30-90 seconds.")) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/tracks/rehydrate-audio`);
      setResult(data.stats);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Rehydrate failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-yt-red/40">
      <div className="font-mono text-[10px] tracking-widest text-yt-red/80 mb-2">▸ AUDIO REHYDRATE</div>
      <button
        type="button"
        data-testid="rehydrate-audio-btn"
        onClick={run}
        disabled={busy}
        className="w-full font-mono text-xs tracking-widest px-3 py-2 border-2 border-yt-red text-yt-red hover:bg-yt-red hover:text-black disabled:opacity-40"
      >
        {busy ? "REHYDRATING…" : "↻ REHYDRATE EXPIRED AUDIO"}
      </button>
      {result && (
        <div data-testid="rehydrate-result" className="mt-2 font-mono text-[11px] text-white/85 leading-relaxed">
          <div>Scanned: <span className="text-yt-yellow">{result.scanned}</span></div>
          <div>Recovered (fresh download): <span className="text-yt-yellow">{result.persisted}</span></div>
          <div>Linked from known-good map: <span className="text-yt-yellow">{result.linked_from_known_good || 0}</span></div>
          <div>Already stable: <span className="text-yt-muted">{result.already_stable}</span></div>
          <div>Unrecoverable: <span className="text-yt-red">{result.download_failed}</span></div>
          <div className="mt-1 text-yt-judge">
            ✓ TOTAL FIXED: <span className="text-yt-yellow font-bold">{(result.persisted || 0) + (result.linked_from_known_good || 0)}</span>
          </div>
        </div>
      )}
      {err && <div className="mt-2 font-mono text-[10px] text-yt-red">{err}</div>}
    </div>
  );
}
